import { useRef, useEffect, useState } from "react"
import { db } from "../firebase"
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore"
import { SEND_PUSH_NOTIFICATION } from "../constants/api"

interface Message {
  text: string
  from: "doctor" | "patient"
  createdAt?: any
}

interface ChatWidgetProps {
  patientId: string
  doctorId: string
  onClose: () => void
  fullHeight?: boolean
}

export default function ChatWidget({ patientId, doctorId, onClose, fullHeight }: ChatWidgetProps) {
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState("")
  const bottomRef = useRef<HTMLDivElement>(null)

  // Listen for real-time updates
  useEffect(() => {
    const chatId = `${patientId}_${doctorId}`
    const q = query(
      collection(db, "chats", chatId, "messages"),
      orderBy("createdAt", "asc")
    )
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setMessages(
        snapshot.docs.map((doc) => ({ ...doc.data() } as unknown as Message))
      )
    })
    return () => unsubscribe()
  }, [patientId, doctorId])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  const handleSend = async () => {
    if (input.trim() === "") return
    const chatId = `${patientId}_${doctorId}`
    const messageText = input.trim()
    setInput("")
    
    try {
      // Send message to Firebase
      await addDoc(collection(db, "chats", chatId, "messages"), {
        text: messageText,
        from: "doctor", // when sending the message from the mobile app use from: "patient"
        createdAt: serverTimestamp(),
      })

      // Send push notification after successful message save
      try {
        const response = await fetch(SEND_PUSH_NOTIFICATION, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: patientId,
            message: messageText
          })
        })

        if (!response.ok) {
          console.error('Failed to send push notification:', response.statusText)
        }
      } catch (notificationError) {
        console.error('Error sending push notification:', notificationError)
        // Don't block the UI if notification fails
      }
    } catch (error) {
      console.error('Error sending message:', error)
      // Restore input if message sending failed
      setInput(messageText)
    }
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSend()
  }

  return (
    <div className={`bg-white rounded-xl flex flex-col h-full shadow-sm`}>
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
        <img src="/images/chat_logo.png" alt="Chat Logo" className="h-5 w-5" />
        <span className="font-bold text-md text-[#232B3A]">Chat</span>
      </div>
      {/* Messages */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3 bg-white">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.from === "doctor" ? "justify-end" : "justify-start"}`}>
            <div className="flex items-center">
              <div className={`rounded-t-xl px-4 py-2 max-w-[300px] min-w-[100px] ${
                msg.from === "doctor"
                  ? "bg-[#E8F0FE] rounded-bl-xl"
                  : "bg-[#F3F4F6] rounded-br-xl"
              }`} style={{wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal'}}>
                <div className="flex items-start gap-2">
                  <span 
                    className="text-base text-black text-sm flex-1" 
                    style={{
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal',
                      lineHeight: '1.4'
                    }}
                  >
                    {msg.text}
                  </span>
                  <img
                    src={
                      msg.from === "doctor" 
                        ? "/images/person_icon.png" 
                        : "/images/patient_chat_icon.png"
                    }
                    alt={
                      msg.from === "doctor" 
                        ? "Doctor" 
                        : "Patient"
                    }
                    className="h-8 w-8 flex-shrink-0 self-end"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      {/* Input */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
        {/* Mic Icon */}
        <button className="flex-shrink-0 w-10 h-10 rounded-xl rounded-full flex items-center justify-center bg-[#F5F6F7] mr-2">
          <img src="/images/mic.png" alt="Mic" className="h-10 w-10" />
        </button>
        {/* Input with attachment icon */}
        <div className="relative flex-1">
          <input
            type="text"
            className="w-full rounded-full bg-[#F5F6F7] pl-4 pr-10 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 border-none shadow-none"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleInputKeyDown}
            style={{ boxShadow: 'none' }}
          />
          <img src="/images/solid_eye.png" alt="Attach" className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 cursor-pointer" />
        </div>
        {/* Send Button */}
        <button
          className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center bg-[#2563eb] ml-2 disabled:opacity-50"
          onClick={handleSend}
          disabled={input.trim() === ""}
          style={{ boxShadow: 'none' }}
        >
          <img src="/images/send_button.png" alt="Send" className="h-10 w-10" />
        </button>
      </div>
    </div>
  )
} 